
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyManagement, type Company } from './hooks/useCompanyManagement';
import CompanyDialog from './components/CompanyDialog';
import CompaniesTable from './components/CompaniesTable';
import ConfirmationDialog from './components/ConfirmationDialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export default function CompaniesManagement() {
  const { companies, loading, createCompany, updateCompany, deleteCompany, suspendCompany, unsuspendCompany, refetch } = useCompanyManagement();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [suspendDialog, setSuspendDialog] = useState<{
    isOpen: boolean;
    company: Company | null;
  }>({ isOpen: false, company: null });
  const [suspensionReason, setSuspensionReason] = useState('');

  const handleViewUsers = (company: any) => {
    navigate(`/admin?tab=platform-users&company=${company.id}&companyName=${encodeURIComponent(company.name)}`);
  };

  const filteredCompanies = companies.filter(company => 
    company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditCompany = (company: any) => {
    setSelectedCompany(company);
    setIsEditDialogOpen(true);
  };

  const handleUpdateCompany = async (data: any) => {
    if (!selectedCompany) return false;
    const success = await updateCompany(selectedCompany.id, data);
    if (success) {
      setSelectedCompany(null);
    }
    return success;
  };

  const openSuspendDialog = (company: Company) => {
    setSuspendDialog({ isOpen: true, company });
  };

  const confirmSuspendCompany = async () => {
    if (!suspendDialog.company) return;
    const company = suspendDialog.company;
    const isSuspended = company.suspended;

    const success = isSuspended
      ? await unsuspendCompany(company.id)
      : await suspendCompany(company.id, 'manual', suspensionReason || undefined);

    if (success) {
      toast.success(
        isSuspended
          ? `${company.name} has been unsuspended`
          : `${company.name} has been suspended`
      );
      refetch();
    } else {
      toast.error(
        isSuspended
          ? 'Failed to unsuspend company'
          : 'Failed to suspend company'
      );
    }
    setSuspendDialog({ isOpen: false, company: null });
    setSuspensionReason('');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Company Management</CardTitle>
            <CardDescription>Manage all companies</CardDescription>
          </div>
          <div className="flex space-x-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="search"
                placeholder="Search companies..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <CompaniesTable
            companies={filteredCompanies}
            loading={loading}
            onEdit={handleEditCompany}
            onDelete={deleteCompany}
            onViewUsers={handleViewUsers}
            onSuspend={openSuspendDialog}
          />
        </CardContent>
      </Card>
      
      <CompanyDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSave={createCompany}
        title="Create Company"
        description="Add a new company to the platform"
      />
      
      <CompanyDialog
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedCompany(null);
        }}
        onSave={handleUpdateCompany}
        company={selectedCompany}
        title="Edit Company"
        description="Update company information"
      />

      <ConfirmationDialog
        isOpen={suspendDialog.isOpen}
        title={suspendDialog.company?.suspended ? 'Unsuspend Company' : 'Suspend Company'}
        description={suspendDialog.company?.suspended
          ? `Unsuspend ${suspendDialog.company?.name}? All members suspended by this action will regain access. Members with individual suspensions will remain suspended.`
          : `Suspend ${suspendDialog.company?.name}? All ${suspendDialog.company?.member_count} active members will immediately lose access. Individually suspended or deactivated members are unaffected.`}
        onClose={() => {
          setSuspendDialog({ isOpen: false, company: null });
          setSuspensionReason('');
        }}
        onConfirm={confirmSuspendCompany}
        actionLabel={suspendDialog.company?.suspended ? 'Unsuspend' : 'Suspend'}
      >
        {!suspendDialog.company?.suspended && (
          <div className="mt-4">
            <Label htmlFor="suspension-reason">
              Reason for suspension (optional)
            </Label>
            <Textarea
              id="suspension-reason"
              placeholder="e.g. Non-payment of subscription renewal"
              value={suspensionReason}
              onChange={(e) => setSuspensionReason(e.target.value)}
              className="mt-2"
              rows={3}
            />
          </div>
        )}
      </ConfirmationDialog>
    </div>
  );
}
